import type { CatalogueType as ConfigCatalogueType } from '@/config/types'
import type { PublicationCode } from '@/scopes/common/domain/types'
import type { FiltersInQuickSearch } from '@/scopes/search/quick-search/domain/types'
import { CatalogueType } from '@/cms/catalogueTypeHelper'
import { useCMSPageField } from '@/cms/fields'
import { useDepartureDateStore } from '@/infrastructure/departure-date/useDepartureDateStore'
import { useIntl } from '@/intl'
import { useCatalogueConfiguration, useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { DepartureAirportFilter } from '@/scopes/search/quick-search/components/Filters/DepartureAirportFilter'
import { DepartureDateFilter } from '@/scopes/search/quick-search/components/Filters/DepartureDateFilter'
import { DestinationFilter } from '@/scopes/search/quick-search/components/Filters/DestinationFilter'
import { DurationFilter } from '@/scopes/search/quick-search/components/Filters/DurationFilter'
import { ParticipantsFilter } from '@/scopes/search/quick-search/components/Filters/ParticipantsFilter'
import { TransportTypeFilter } from '@/scopes/search/quick-search/components/Filters/TransportTypeFilter'
import QuickSearchBarResponsive from '@/scopes/search/quick-search/components/QuickSearchBarResponsive'
import QuickSearchToggle from '@/scopes/search/quick-search/components/QuickSearchToggle'
import {
  isLoadingSelector,
  searchLinkSelector,
  totalResultsSelector,
} from '@/scopes/search/quick-search/components/selectors'
import { getFiltersForQuickSearch } from '@/scopes/search/quick-search/domain/policies/filters'
import { getDefaultFilterSelection } from '@/scopes/search/quick-search/domain/policies/publicationCodes'
import { getCatalogues } from '@/scopes/search/quick-search/domain/policies/sites'
import { quickSearchUseCases } from '@/scopes/search/quick-search/domain/useCases/quickSearchUseCases'
import { createQuickSearchApiClient } from '@/scopes/search/quick-search/infrastructure/api/quickSearchApiClient'
import { quickSearchStore } from '@/scopes/search/quick-search/infrastructure/store/store'
import { useQuickSearchSelector } from '@/scopes/search/quick-search/infrastructure/store/useQuickSearchSelector'
import { Button, ButtonLoaderIcon, tk } from '@sunwebgroup/ui-components'
import * as React from 'react'
import { styled } from 'styled-components'
import { Hero } from './Hero'

const { initQuickSearch, filterConfirmed, switchPublicationCode } = quickSearchUseCases({
  quickSearchApiClient: createQuickSearchApiClient({}),
  store: quickSearchStore,
})

const QuickSearchButton = styled(Button)`
  width: 100%;
  margin-top: ${tk('spacing_xs')};
  @media ${tk('mq_desktop_md')} {
    flex-shrink: 0;
    width: unset;
    margin-top: unset;
  }

  &:hover&:not(:disabled) {
    color: ${tk('color_palette_grey_50')};
  }
`

export default function QuickSearch({ catalogueType }: { catalogueType: CatalogueType }) {
  if (catalogueType === CatalogueType.None) {
    return <QuickSearchWithToggles />
  }
  const mappedCatalogueType = catalogueType === CatalogueType.Summer ? 'summer' : 'winter'
  return <QuickSearchComponent catalogue={mappedCatalogueType} />
}

function QuickSearchWithToggles() {
  const siteName = useConfiguration('siteName')
  const [defaultCatalogue] = getCatalogues(siteName)
  return <QuickSearchComponent catalogue={defaultCatalogue} showToggles />
}

function QuickSearchComponent({
  catalogue,
  showToggles = false,
}: {
  catalogue: ConfigCatalogueType
  showToggles?: boolean
}) {
  const intl = useIntl()
  const { reset } = useDepartureDateStore()
  const contextItemId = useCMSPageField('HomePage.quickSearch.datasourceId') ?? ''
  const initialPublicationCode = useCatalogueConfiguration(catalogue, 'publicationCode')
  const [publicationCode, setPublicationCode] = React.useState<PublicationCode>(initialPublicationCode as PublicationCode)
  const initialSelection = getDefaultFilterSelection(initialPublicationCode as PublicationCode)
  React.useEffect(() => {
    initQuickSearch({
      publicationCode: initialPublicationCode,
      contextItemId,
      initialSelection,
    })
  }, [])

  const [filterComponents, setFilterComponents] = React.useState(getFiltersForQuickSearch(publicationCode as PublicationCode) || [])
  React.useEffect(() => {
    setFilterComponents(getFiltersForQuickSearch(publicationCode as PublicationCode))
  }, [publicationCode])

  const isLoading = useQuickSearchSelector(isLoadingSelector)
  const totalResults = useQuickSearchSelector(totalResultsSelector)
  const searchLink = useQuickSearchSelector(searchLinkSelector)

  const disableSearchButton = isLoading || totalResults === 0

  async function onSwitchToggle(pc: string, selection: Partial<FiltersInQuickSearch>) {
    reset()
    setPublicationCode(pc as PublicationCode)
    await switchPublicationCode(pc, selection)
  }

  return (
    <Hero useGenericTitle={showToggles} catalogue={catalogue} filters={filterComponents.length}>
      {showToggles && <QuickSearchToggle isLoading={isLoading} onSwitch={onSwitchToggle} />}
      <QuickSearchBarResponsive disabled={isLoading}>
        {filterComponents.includes('DestinationFilter') && (
          <DestinationFilter
            onConfirm={(selection) => {
              filterConfirmed({ type: 'Destination', selection })
            }}
          />
        )}
        {filterComponents.includes('DepartureDateFilter') && (
          <DepartureDateFilter
            onConfirm={({ departureDate }) => {
              filterConfirmed({ type: 'DepartureDate', selection: departureDate })
            }}
            onReset={() => {
              filterConfirmed({ type: 'DepartureDate', selection: undefined })
            }}
          />
        )}
        {filterComponents.includes('DurationFilter') && (
          <DurationFilter
            onConfirm={(selection) => {
              filterConfirmed({ type: 'Duration', selection })
            }}
          />
        )}
        {filterComponents.includes('ParticipantsFilter') && (
          <ParticipantsFilter
            onConfirm={(selection) => {
              filterConfirmed({ type: 'Participants', selection })
            }}
          />
        )}
        {filterComponents.includes('TransportTypeFilter') && (
          <TransportTypeFilter
            onConfirm={(selection) => {
              filterConfirmed({ type: 'TransportType', selection })
            }}
          />
        )}
        {filterComponents.includes('DepartureAirportFilter') && (
          <DepartureAirportFilter
            onConfirm={(selection) => {
              filterConfirmed({ type: 'DepartureAirport', selection })
            }}
          />
        )}
        {isLoading
          ? (
              <QuickSearchButton disabled={isLoading}>
                <ButtonLoaderIcon />
              </QuickSearchButton>
            )
          : (
              <QuickSearchButton
                data-qa="quick-search-submit-button"
                disabled={disableSearchButton}
                as={disableSearchButton ? 'button' : 'a'}
                href={searchLink}
              >
                {intl.formatMessage({ id: 'quicksearch_results' }, { nrOfResults: totalResults })}
              </QuickSearchButton>
            )}
      </QuickSearchBarResponsive>
    </Hero>
  )
}
